import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Helmet } from 'react-helmet';
import { toast } from 'react-hot-toast';

import { useAuth } from '../contexts/AuthContext';
import { useAudioJobs } from '../hooks/useAudioJobs';
import CreateAudioJobModalEnhanced from '../components/audio/CreateAudioJobModalEnhanced';
import AudioJobCard from '../components/audio/AudioJobCard';
import DashboardLayout from '../components/common/DashboardLayout/DashboardLayout';
import AddIcon2 from '@mui/icons-material/Add';
import MusicNoteIcon from '@mui/icons-material/MusicNote';

const TAB_FILTERS = [
  { label: 'All',        filter: null },
  { label: 'Processing', filter: 'processing' },
  { label: 'Completed',  filter: 'completed' },
  { label: 'Failed',     filter: 'failed' },
];

function AudioTranslation() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tabValue, setTabValue] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { jobs, loading, error, stats, fetchJobs, createJob, deleteJob, cancelJob } = useAudioJobs();

  useEffect(() => { fetchJobs(); }, []);

  const getCount = (filter) => {
    if (!filter) return jobs.length;
    if (filter === 'processing') return jobs.filter(j => ['pending', 'extracting', 'translating'].includes(j.status)).length;
    if (filter === 'completed') return jobs.filter(j => j.status === 'completed').length;
    if (filter === 'failed') return jobs.filter(j => j.status === 'failed').length;
    return 0;
  };

  const getFilteredJobs = () => {
    const f = TAB_FILTERS[tabValue].filter;
    if (!f) return jobs;
    if (f === 'processing') return jobs.filter(j => ['pending', 'extracting', 'translating'].includes(j.status));
    if (f === 'completed')  return jobs.filter(j => j.status === 'completed');
    if (f === 'failed')     return jobs.filter(j => j.status === 'failed');
    return jobs;
  };

  const handleCreateJob = async (jobData) => {
    try {
      await createJob(jobData);
      setIsCreateModalOpen(false);
      toast.success('Audio translation job created successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to create audio translation job');
    }
  };

  const filteredJobs = getFilteredJobs();
  const processingCount = getCount('processing');

  return (
    <DashboardLayout>
      <Helmet>
        <title>Audio Translation - DubLabs</title>
      </Helmet>

      <Container maxWidth={false} sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 }, maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
            <Box>
              <Typography sx={{
                fontFamily: 'Inter, sans-serif', fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.6rem' },
                letterSpacing: '-0.03em', color: '#FFFFFF', lineHeight: 1.1, mb: 0.75,
              }}>
                Audio Translation
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.95rem', color: 'rgba(255,255,255,0.45)', fontWeight: 400 }}>
                Extract and translate audio from YouTube videos without video processing.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              <Button
                onClick={fetchJobs}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.6)',
                  fontFamily: 'Inter, sans-serif', fontWeight: 500,
                  fontSize: '14px', textTransform: 'none',
                  borderRadius: '8px', px: 2, py: 1.2,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFFFFF' },
                  '&:disabled': { opacity: 0.4 },
                }}
              >
                Refresh
              </Button>
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={{
                  backgroundColor: '#FFFFFF', color: '#000000',
                  fontFamily: 'Inter, sans-serif', fontWeight: 700,
                  fontSize: '14px', textTransform: 'none',
                  borderRadius: '8px', px: 2.5, py: 1.2,
                  letterSpacing: '-0.01em',
                  '&:hover': { backgroundColor: '#F0F0F0' },
                }}
              >
                New Translation
              </Button>
            </Box>
          </Box>

          {/* Stat pills */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <StatPill label={`${stats.total} Total Jobs`} color="rgba(255,255,255,0.12)" textColor="rgba(255,255,255,0.7)" />
            <StatPill label={`${stats.completed} Completed`} color="rgba(34,197,94,0.12)" textColor="#22C55E" />
            <StatPill label={`${stats.failed} Failed`} color="rgba(239,68,68,0.12)" textColor="#EF4444" />
            {processingCount > 0 && (
              <StatPill label={`${processingCount} Processing`} color="rgba(59,130,246,0.12)" textColor="#3B82F6" pulse />
            )}
          </Box>
        </Box>

        {/* Pill Tabs */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {TAB_FILTERS.map((tab, i) => {
            const count = getCount(tab.filter);
            const isActive = tabValue === i;
            return (
              <Box
                key={i}
                onClick={() => setTabValue(i)}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.75,
                  px: 1.75, py: 0.7, borderRadius: '7px', cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif', fontSize: '13px',
                  fontWeight: isActive ? 600 : 500,
                  backgroundColor: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
                  color: isActive ? '#000000' : 'rgba(255,255,255,0.5)',
                  border: isActive ? 'none' : '1px solid rgba(255,255,255,0.08)',
                  transition: 'all 0.15s ease', userSelect: 'none',
                  '&:hover': {
                    backgroundColor: isActive ? '#F0F0F0' : 'rgba(255,255,255,0.09)',
                    color: isActive ? '#000000' : '#FFFFFF',
                  },
                }}
              >
                {tab.label}
                <Typography component="span" sx={{
                  fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 600,
                  px: 0.6, py: 0.1, borderRadius: '4px',
                  backgroundColor: isActive ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.08)',
                  color: isActive ? '#000000' : 'rgba(255,255,255,0.4)',
                  lineHeight: 1.5, minWidth: '16px', textAlign: 'center',
                }}>
                  {count}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Error */}
        {error && (
          <Box sx={{ mb: 3, px: 2, py: 1.5, borderRadius: '8px', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#EF4444' }}>{error}</Typography>
          </Box>
        )}

        {/* Content */}
        {loading && jobs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: 'rgba(255,255,255,0.3)' }} />
          </Box>
        ) : filteredJobs.length === 0 ? (
          <Box sx={{
            py: 10, textAlign: 'center',
            border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '12px',
          }}>
            <MusicNoteIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.12)', mb: 2 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '15px', mb: 0.75 }}>
              {tabValue === 0 ? 'No audio translation jobs yet' : `No ${TAB_FILTERS[tabValue].label.toLowerCase()} jobs`}
            </Typography>
            {tabValue === 0 && (
              <>
                <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontFamily: 'Inter, sans-serif', fontSize: '13px', mb: 3 }}>
                  Create your first audio translation job to get started.
                </Typography>
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  startIcon={<AddIcon2 sx={{ fontSize: 16 }} />}
                  sx={{
                    backgroundColor: '#FFFFFF', color: '#000000',
                    fontFamily: 'Inter, sans-serif', fontWeight: 700,
                    fontSize: '14px', textTransform: 'none',
                    borderRadius: '8px', px: 2.5, py: 1.1,
                    '&:hover': { backgroundColor: '#F0F0F0' },
                  }}
                >
                  Create Audio Translation
                </Button>
              </>
            )}
          </Box>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            gap: 2,
          }}>
            {filteredJobs.map((job) => (
              <AudioJobCard
                key={job.id}
                job={job}
                onDelete={deleteJob}
                onCancel={cancelJob}
                isAdmin={isAdmin}
              />
            ))}
          </Box>
        )}
      </Container>

      <CreateAudioJobModalEnhanced
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateJob}
      />
    </DashboardLayout>
  );
}

const StatPill = ({ label, color, textColor, pulse }) => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'center', gap: pulse ? 0.75 : 0,
    px: 1.5, py: 0.5, borderRadius: '6px', backgroundColor: color,
    fontFamily: 'Inter, sans-serif', fontSize: '12px', fontWeight: 600, color: textColor,
  }}>
    {pulse && (
      <Box sx={{
        width: 6, height: 6, borderRadius: '50%', backgroundColor: textColor,
        animation: 'pulse 1.5s ease-in-out infinite',
        '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } },
      }} />
    )}
    {label}
  </Box>
);

export default AudioTranslation;
